import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate, type Identifier } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import type { CrmDataProvider } from "../providers/types";
import type { QuoterTermImpact } from "../types";
import { QUOTER_ADMIN_QUERY_KEY } from "./quoterAdminData";
import { formatMxn } from "./quoterFormat";

/**
 * Almikia Epic EQ2 (TASK-099): the "ver impacto" screen, shown BEFORE the
 * installments term of a project changes. It is read-only until the user
 * confirms: `onConfirm` is the only place that recalculates anything, and
 * cancelling (or closing) leaves every quote untouched.
 */
export const TermImpactDialog = ({
  projectId,
  newTerm,
  onConfirm,
  onCancel,
}: {
  projectId: Identifier;
  newTerm: number;
  /** Applies the new term (recalculates the valid quotes); throws on failure. */
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [applying, setApplying] = useState(false);
  const [applyFailed, setApplyFailed] = useState(false);

  const impact = useQuery({
    queryKey: [QUOTER_ADMIN_QUERY_KEY, "term-impact", projectId, newTerm],
    queryFn: (): Promise<QuoterTermImpact> =>
      dataProvider.quoterTermChangeImpact(projectId, newTerm),
  });

  const confirm = async () => {
    setApplying(true);
    setApplyFailed(false);
    try {
      await onConfirm();
    } catch {
      setApplyFailed(true);
      setApplying(false);
    }
  };

  const data = impact.data;
  const perQuote =
    data && data.changed_quotes > 0
      ? data.installment_delta / data.changed_quotes
      : 0;

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onCancel())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{translate("crm.quoter.impact.title")}</DialogTitle>
          <DialogDescription className="text-base sm:text-sm">
            {translate("crm.quoter.impact.question", {
              current: data?.current_term ?? "…",
              next: newTerm,
            })}
          </DialogDescription>
        </DialogHeader>

        {impact.isError ? (
          <ListErrorState
            message={translate("crm.quoter.impact.error")}
            onRetry={() => impact.refetch()}
          />
        ) : !data ? (
          <ListCardsSkeleton rows={2} />
        ) : (
          <dl className="space-y-2 text-base tabular-nums sm:text-sm">
            <div className="flex justify-between gap-3">
              <dt>{translate("crm.quoter.impact.valid_quotes")}</dt>
              <dd>{data.valid_quotes}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>{translate("crm.quoter.impact.changed_quotes")}</dt>
              <dd>{data.changed_quotes}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>{translate("crm.quoter.impact.total_before")}</dt>
              <dd>{formatMxn(data.installment_total_before)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>{translate("crm.quoter.impact.total_after")}</dt>
              <dd>{formatMxn(data.installment_total_after)}</dd>
            </div>
            <div className="flex justify-between gap-3 font-medium">
              <dt>{translate("crm.quoter.impact.total_delta")}</dt>
              <dd data-testid="term-impact-delta">
                {formatMxn(data.installment_delta)}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>{translate("crm.quoter.impact.per_quote")}</dt>
              <dd>{formatMxn(perQuote)}</dd>
            </div>
            <p className="pt-1 text-muted-foreground">
              {translate(
                data.changed_quotes === 0
                  ? "crm.quoter.impact.none"
                  : "crm.quoter.impact.note",
              )}
            </p>
          </dl>
        )}
        {applyFailed ? (
          <p role="alert" className="text-sm text-destructive">
            {translate("crm.quoter.impact.apply_error")}
          </p>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={onCancel}
            disabled={applying}
          >
            {translate("crm.quoter.impact.cancel")}
          </Button>
          <Button
            type="button"
            className="h-11 sm:h-9"
            onClick={confirm}
            disabled={applying || !data}
          >
            {translate("crm.quoter.impact.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
